
import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { Upload } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../../context/ToastContext';

// Helper for Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const API_URL_TARIFAS = `${API_BASE_URL}/api/v2/tarifas`;

const TarifaForm = ({ onClose, onSuccess, initialData, empresas = [], isSuperAdmin = false, currentUserEmpresaId = null }) => {
    const [isLoading, setIsLoading] = useState(false);
    const isEditing = !!initialData;
    const toast = useToast();

    const [formData, setFormData] = useState({
        id_empresa: '', 
        tarifa_base: '',
        tarifa_exceso: '',
        monto_reconexion: '',
        monto_registro_socio: '',
        imagen_junta: null,
        imagen_sello: null,
        nombre_aplicacion: '',
        valor_base: '',
        porcentaje_mora: '',
        tipo_tarifa: '',
    });

    const [juntaPreview, setJuntaPreview] = useState(null);
    const [selloPreview, setSelloPreview] = useState(null);

    useEffect(() => {
        if (initialData) {
            setFormData({
                id_empresa: initialData.id_empresa || '',
                tarifa_base: initialData.tarifa_base || '',
                tarifa_exceso: initialData.tarifa_exceso || '',
                monto_reconexion: initialData.monto_reconexion || '',
                monto_registro_socio: initialData.monto_registro_socio || '',
                imagen_junta: null, // Files start null on edit
                imagen_sello: null,
                nombre_aplicacion: initialData.nombre_aplicacion || '',
                valor_base: initialData.valor_base || '',
                porcentaje_mora: initialData.porcentaje_mora || '',
                tipo_tarifa: initialData.tipo_tarifa || '',
            });
            // Set initial previews
            if (initialData.imagen_junta) setJuntaPreview(`${API_BASE_URL}${initialData.imagen_junta}`);
            if (initialData.imagen_sello) setSelloPreview(`${API_BASE_URL}${initialData.imagen_sello}`);
        } else {
             // Default for new entry
             setFormData(prev => ({
                ...prev,
                id_empresa: isSuperAdmin ? '' : (currentUserEmpresaId || '')
             }));
             setJuntaPreview(null);
             setSelloPreview(null);
        }
    }, [initialData, isSuperAdmin, currentUserEmpresaId]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleFileChange = (field, file) => {
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('La imagen no puede superar los 5MB');
                return;
            }
            setFormData(prev => ({ ...prev, [field]: file }));
            const objectUrl = URL.createObjectURL(file);
            if (field === 'imagen_junta') setJuntaPreview(objectUrl);
            if (field === 'imagen_sello') setSelloPreview(objectUrl);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Basic Validation
        const requiredFields = [
            'tarifa_base', 'tarifa_exceso', 'monto_reconexion', 
            'monto_registro_socio', 'nombre_aplicacion', 'valor_base', 
            'porcentaje_mora', 'tipo_tarifa'
        ];

            // Auto-assign empresa for non-super admin if missing
            if (!formData.id_empresa && !isSuperAdmin && currentUserEmpresaId) {
                formData.id_empresa = currentUserEmpresaId;
            }

            // Validate Empresa ID
            if (!formData.id_empresa) {
                 toast.error('Error: No se ha asignado una empresa a la tarifa.');
                 setIsLoading(false);
                 return;
            }
    
            for (const field of requiredFields) {
                if (!formData[field]) {
                     toast.error('Por favor complete todos los campos requeridos');
                     setIsLoading(false);
                     return;
                }
            }
    
            try {
                const data = new FormData();
                Object.keys(formData).forEach(key => {
                    if (key === 'imagen_junta' || key === 'imagen_sello') {
                        if (formData[key] instanceof File) {
                            data.append(key, formData[key]);
                        }
                    } else {
                        data.append(key, formData[key]);
                    }
                });
                
                // Ensure id_empresa is appended if it wasn't in formData originally (safety check)
                if (!data.has('id_empresa') && formData.id_empresa) {
                    data.append('id_empresa', formData.id_empresa);
                }

            const token = localStorage.getItem('token');
            const config = { 
                headers: { 
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                } 
            };

            if (isEditing) {
                await axios.put(`${API_URL_TARIFAS}/${initialData.id_tarifa}`, data, config);
                toast.success('Tarifa actualizada exitosamente');
            } else {
                await axios.post(API_URL_TARIFAS, data, config);
                toast.success('Tarifa creada exitosamente');
            }
            onSuccess();
        } catch (error) {
            console.error('Error saving tarifa:', error);
            const errorMsg = error.response?.data?.error || (typeof error?.response?.data === 'string' ? error.response.data : 'Error al guardar la tarifa');
            toast.error(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={isEditing ? 'Editar Tarifa' : 'Nueva Tarifa'}
            size="lg"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        Cancelar
                    </Button>
                    <Button onClick={handleSubmit} isLoading={isLoading}>
                        Guardar
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                
                {/* Empresa Selection (Only for SuperAdmin) */}
                {isSuperAdmin && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Empresa / Junta</label>
                        <select 
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                            value={formData.id_empresa}
                            onChange={(e) => handleChange('id_empresa', e.target.value)}
                            disabled={isEditing} // Prevent changing company on edit if desired, or allow it. Usually better to allow.
                        >
                            <option value="">Seleccione una empresa...</option>
                            {empresas.map(emp => (
                                <option key={emp.id} value={emp.id}>
                                    {emp.nombre}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                        label="Tarifa Base (m3)"
                        type="number"
                        step="0.01"
                        value={formData.tarifa_base}
                        onChange={e => handleChange('tarifa_base', e.target.value)}
                        required
                    />
                    <Input
                        label="Valor Unitario Exceso ($)"
                        type="number"
                        step="0.01"
                        value={formData.tarifa_exceso}
                        onChange={e => handleChange('tarifa_exceso', e.target.value)}
                        required
                    />
                    <Input
                        label="Valor Base ($)"
                        type="number"
                        step="0.01"
                        value={formData.valor_base}
                        onChange={e => handleChange('valor_base', e.target.value)}
                        required
                    />
                    <Input
                        label="Valor de Mora ($)"
                        type="number"
                        step="0.01"
                        value={formData.porcentaje_mora}
                        onChange={e => handleChange('porcentaje_mora', e.target.value)}
                        required
                    />
                     <Input
                        label="Monto Reconexión ($)"
                        type="number"
                        step="0.01"
                        value={formData.monto_reconexion}
                        onChange={e => handleChange('monto_reconexion', e.target.value)}
                        required
                    />
                    <Input
                        label="Monto Registro Socio ($)"
                        type="number"
                        step="0.01"
                        value={formData.monto_registro_socio}
                        onChange={e => handleChange('monto_registro_socio', e.target.value)}
                        required
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Tarifa</label>
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                            value={formData.tipo_tarifa}
                            onChange={e => handleChange('tipo_tarifa', e.target.value)}
                            required
                        >
                            <option value="">Seleccione un tipo</option>
                            <option value="Domestica">Doméstica</option>
                            <option value="Comercial">Comercial</option>
                        </select>
                    </div>
                    <Input
                        label="Nombre Aplicación"
                        value={formData.nombre_aplicacion}
                        onChange={e => handleChange('nombre_aplicacion', e.target.value)}
                        required
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 mt-2">
                     {/* Imagen Junta */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Imagen Junta</label>
                        <div className="flex items-center space-x-2">
                             <label className="cursor-pointer bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg text-sm hover:bg-gray-50 flex items-center shadow-sm">
                                <Upload className="w-4 h-4 mr-2" />
                                <span>{juntaPreview ? 'Cambiar imagen' : 'Subir imagen'}</span>
                                <input 
                                    type="file" 
                                    className="hidden" 
                                    accept="image/*"
                                    onChange={e => handleFileChange('imagen_junta', e.target.files[0])}
                                />
                             </label>
                             {juntaPreview && (
                                <div className="ml-2 relative group w-10 h-10">
                                    <img 
                                        src={juntaPreview} 
                                        alt="Vista previa Junta" 
                                        className="w-full h-full object-cover rounded border border-gray-200 bg-gray-100"
                                    />
                                </div>
                             )}
                        </div>
                    </div>

                    {/* Imagen Sello */}
                     <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Imagen Sello</label>
                        <div className="flex items-center space-x-2">
                             <label className="cursor-pointer bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg text-sm hover:bg-gray-50 flex items-center shadow-sm">
                                <Upload className="w-4 h-4 mr-2" />
                                <span>{selloPreview ? 'Cambiar imagen' : 'Subir imagen'}</span>
                                <input 
                                    type="file" 
                                    className="hidden" 
                                    accept="image/*"
                                    onChange={e => handleFileChange('imagen_sello', e.target.files[0])}
                                />
                             </label>
                             {selloPreview && (
                                <div className="ml-2 relative group w-10 h-10">
                                     <img 
                                        src={selloPreview} 
                                        alt="Vista previa Sello" 
                                        className="w-full h-full object-cover rounded border border-gray-200 bg-gray-100"
                                    />
                                </div>
                             )}
                        </div>
                    </div>
                </div>

            </form>
        </Modal>
    );
};

export default TarifaForm;
